import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Animated,
  FlatList,
  Image,
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import Icon from 'react-native-vector-icons/Feather';
import { Colors, Radius, Shadow, Spacing, Typography } from '../theme';
import { useTheme } from '../theme/useTheme';
import { daylightStyle } from '../theme/daylightStyle';
import type { TonyteTheme } from '../theme/themes';
import type { TonyteActivity } from '../types';
import {
  pickRecommendationReason,
  type RankedEvent,
  type RecommendationProfile,
  type RecommendationReason,
  type TopPicksMode,
} from '../utils/eventRecommendations';
import { useActivityInterested } from '../hooks/useFeed';
import { useTranslatedActivity } from '../hooks/useTranslatedActivity';
import { useReducedMotionPreference } from '../hooks/useReducedMotionPreference';
import { useI18n } from '../i18n';
import { getWebFallbackImage, resolveActivityImage } from '../utils/activityImages';
import { getResolvedEventId } from '../utils/eventIds';
import AmbientGlow from './AmbientGlow';

export type TopPicksModuleStatus = 'loading' | 'ready' | 'error';

// --- sizing ------------------------------------------------------------
// One large card dominates on mobile (82-88% of the available width, per
// spec) so the next card visibly peeks at the edge; on wide web/desktop
// layouts the same carousel instead shows several capped-width cards at
// once rather than one card stretched to an unreasonable width.
const MOBILE_CARD_WIDTH_RATIO = 0.86;
const WIDE_CARD_MAX_WIDTH = 430;
const CARD_ASPECT_RATIO = 0.56; // height = width * ratio
const MIN_CARD_HEIGHT = 168;
const MAX_CARD_HEIGHT = 250;
const CARD_GAP = 14;
const CONTAINER_INNER_PADDING = Spacing.md;
// Extra breathing room after the last card, beyond the container's own
// inner padding — otherwise the final card sits flush against the edge.
const LAST_CARD_EXTRA_PADDING = Spacing.lg;

function useResponsiveCardSize() {
  const { width: viewportWidth } = useWindowDimensions();
  const isWideWeb = Platform.OS === 'web' && viewportWidth >= 1024;
  const columns = Platform.OS === 'web' && viewportWidth >= 1480 ? 3 : isWideWeb ? 2 : 1;
  // Matches FeedScreen's own outer list horizontal inset at each breakpoint,
  // so the carousel's available width lines up with the list around it.
  const horizontalInset = isWideWeb ? 24 : Spacing.base;
  const availableWidth = Math.max(280, viewportWidth - horizontalInset * 2 - CONTAINER_INNER_PADDING * 2);

  const cardWidth = isWideWeb
    ? Math.min(WIDE_CARD_MAX_WIDTH, Math.round((availableWidth - CARD_GAP * (columns - 1)) / columns))
    : Math.round(availableWidth * MOBILE_CARD_WIDTH_RATIO);
  const cardHeight = Math.min(MAX_CARD_HEIGHT, Math.max(MIN_CARD_HEIGHT, Math.round(cardWidth * CARD_ASPECT_RATIO)));

  return { cardWidth, cardHeight, isWideWeb };
}

const LOCALES: Record<string, string> = { de: 'de-DE', es: 'es-ES', fr: 'fr-FR', ru: 'ru-RU' };

function formatCardMeta(activity: TonyteActivity, locale: string): string {
  const date = activity.startDate
    ? new Date(`${activity.startDate}T12:00:00`).toLocaleDateString(locale, { weekday: 'short', month: 'short', day: 'numeric' })
    : '';
  const time = activity.startTime?.trim() || '';
  return [date, time].filter(Boolean).join(' · ');
}

function reasonText(reason: RecommendationReason, t: (key: string, fallback?: string) => string): string {
  switch (reason.kind) {
    case 'explicitInterest':
      return t('feed.topPicksReason.interest', 'Because you like {interest}').replace('{interest}', reason.interest.toLowerCase());
    case 'category':
      return t('feed.topPicksReason.category', 'Matches your {category} interests').replace('{category}', t(`categories.${reason.category}`, reason.category));
    case 'joined':
      return t('feed.topPicksReason.joined', 'Similar to events you’ve joined');
    case 'shared':
      return t('feed.topPicksReason.shared', 'Similar to events you’ve shared');
    case 'viewed':
      return t('feed.topPicksReason.viewed', 'Based on events you’ve viewed');
    case 'popular':
    default:
      return t('feed.popularNearYou', 'Popular near you');
  }
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

type TopPickEntry = { item: RankedEvent; reason: RecommendationReason };

type CardProps = {
  activity: TonyteActivity;
  reason: RecommendationReason;
  userId: string | null;
  width: number;
  height: number;
  onPress: (activity: TonyteActivity) => void;
};

const TopPickWideCard: React.FC<CardProps> = memo(({ activity, reason, userId, width, height, onPress }) => {
  const { t, language } = useI18n();
  const { theme } = useTheme();
  const themed = useMemo(() => createCardThemeStyles(theme), [theme]);
  const locale = LOCALES[language] ?? 'en-US';
  const translatedActivity = useTranslatedActivity(activity);
  const { interested, toggle } = useActivityInterested(getResolvedEventId(activity), userId, activity);
  const [imageSource, setImageSource] = useState<number | string | null>(null);
  const [imageFailed, setImageFailed] = useState(false);
  const [usedImageFallback, setUsedImageFallback] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setImageFailed(false);
    setUsedImageFallback(false);
    setImageSource(null);
    resolveActivityImage(activity)
      .then(resolved => { if (!cancelled) setImageSource(resolved.source); })
      .catch(() => { if (!cancelled) setImageFailed(true); });
    return () => { cancelled = true; };
  }, [activity]);

  const handleInterestPress = () => {
    if (!userId) {
      Alert.alert(t('auth.signInRequired'), t('auth.signInToUseFeature'));
      return;
    }
    void toggle();
  };

  const title = translatedActivity.translatingTitle ? '...' : translatedActivity.title;
  const metaLine = formatCardMeta(activity, locale);
  const location = activity.venueName || activity.locationName || activity.address || activity.city || '';
  const hostName = activity.hostName || activity.venueName || '';
  const reasonLabel = reasonText(reason, t);

  return (
    <TouchableOpacity
      activeOpacity={0.9}
      style={[cardStyles.cardShape, cardStyles.card, themed.card, { width, height }]}
      onPress={() => onPress(activity)}
      accessibilityRole="button"
      accessibilityLabel={title}
    >
      {imageSource && !imageFailed ? (
        <Image
          source={typeof imageSource === 'number' ? imageSource : { uri: imageSource }}
          style={cardStyles.image}
          resizeMode="cover" resizeMethod="resize"
          onError={() => {
            if (!usedImageFallback) {
              setUsedImageFallback(true);
              setImageSource(getWebFallbackImage(activity));
            } else {
              setImageFailed(true);
            }
          }}
        />
      ) : (
        <LinearGradient colors={['#1d2c40', '#101722', '#070b14']} style={cardStyles.image}>
          <AmbientGlow tone="orange" size={220} intensity={0.6} style={cardStyles.fallbackGlow} />
          <Icon name="calendar" size={26} color="rgba(255,255,255,0.4)" />
        </LinearGradient>
      )}
      <LinearGradient
        colors={['rgba(3,7,15,0.10)', 'rgba(3,7,15,0.30)', 'rgba(3,7,15,0.80)', 'rgba(3,7,15,0.96)']}
        locations={[0, 0.32, 0.68, 1]}
        style={StyleSheet.absoluteFill}
      />

      <View style={cardStyles.topRow} pointerEvents="box-none">
        <View style={cardStyles.topLeftStack}>
          <View style={cardStyles.forYouBadge}>
            <Icon name="zap" size={10} color={Colors.sunset} />
            <Text style={cardStyles.forYouBadgeText}>{t('feed.topPicksBadge', 'For You')}</Text>
          </View>
          {reasonLabel ? (
            <View style={cardStyles.reasonChip}>
              <Icon name="zap" size={9} color="rgba(255,208,166,0.92)" />
              <Text style={cardStyles.reasonChipText} numberOfLines={1}>{reasonLabel}</Text>
            </View>
          ) : null}
        </View>
        <TouchableOpacity
          activeOpacity={0.82}
          style={[cardStyles.heartButton, interested && cardStyles.heartButtonActive]}
          onPress={event => { event.stopPropagation(); handleInterestPress(); }}
          accessibilityRole="button"
          accessibilityLabel={t('event.interested', 'Interested')}
        >
          <Icon name="heart" size={15} color={interested ? '#fff' : 'rgba(255,255,255,0.85)'} />
        </TouchableOpacity>
      </View>

      <View style={cardStyles.bottomCopy}>
        <Text style={cardStyles.title} numberOfLines={2}>{title}</Text>
        {metaLine ? (
          <View style={cardStyles.metaRow}>
            <Icon name="calendar" size={12} color={Colors.turquoise} />
            <Text style={cardStyles.metaText} numberOfLines={1}>{metaLine}</Text>
          </View>
        ) : null}
        {location ? (
          <View style={cardStyles.metaRow}>
            <Icon name="map-pin" size={12} color={Colors.sunset} />
            <Text style={cardStyles.metaText} numberOfLines={1}>{location}</Text>
          </View>
        ) : null}
        {hostName ? (
          <View style={cardStyles.metaRow}>
            <Icon name="check-circle" size={11} color="rgba(255,255,255,0.7)" />
            <Text style={cardStyles.partnerText} numberOfLines={1}>{hostName}</Text>
          </View>
        ) : null}
      </View>
    </TouchableOpacity>
  );
});

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

const TopPicksModuleSkeleton: React.FC<{ cardWidth: number; cardHeight: number }> = memo(({ cardWidth, cardHeight }) => {
  const { theme } = useTheme();
  const themed = useMemo(() => createModuleThemeStyles(theme), [theme]);
  const pulse = useRef(new Animated.Value(0.55)).current;

  useEffect(() => {
    const animation = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, { toValue: 1, duration: 800, useNativeDriver: Platform.OS !== 'web' }),
        Animated.timing(pulse, { toValue: 0.55, duration: 800, useNativeDriver: Platform.OS !== 'web' }),
      ]),
    );
    animation.start();
    return () => animation.stop();
  }, [pulse]);

  return (
    <View style={[moduleStyles.container, themed.container]} accessibilityElementsHidden importantForAccessibility="no-hide-descendants">
      <View style={moduleStyles.header}>
        <View style={moduleStyles.headerCopy}>
          <Animated.View style={[moduleStyles.skeletonTitle, moduleStyles.skeletonFill, themed.skeletonFill, { opacity: pulse }]} />
          <Animated.View style={[moduleStyles.skeletonSubtitle, moduleStyles.skeletonFill, themed.skeletonFill, { opacity: pulse }]} />
        </View>
      </View>
      <View style={moduleStyles.skeletonCardRow}>
        <Animated.View style={[cardStyles.cardShape, moduleStyles.skeletonFill, themed.skeletonFill, { width: cardWidth, height: cardHeight, opacity: pulse }]} />
      </View>
    </View>
  );
});

// ---------------------------------------------------------------------------
// Module
// ---------------------------------------------------------------------------

type Props = {
  items: RankedEvent[];
  status: TopPicksModuleStatus;
  mode: TopPicksMode;
  profile: RecommendationProfile | null;
  userId: string | null;
  onRetry: () => void;
  onCardPress: (activity: TonyteActivity) => void;
  onSeeAll: () => void;
  errorMessage: string;
  retryLabel: string;
  testID?: string;
};

const TopPicksModule: React.FC<Props> = ({
  items,
  status,
  mode,
  profile,
  userId,
  onRetry,
  onCardPress,
  onSeeAll,
  errorMessage,
  retryLabel,
  testID,
}) => {
  // Keep this hook in the component's stable hook order during Fast Refresh.
  useI18n();
  const { theme } = useTheme();
  const themed = useMemo(() => createModuleThemeStyles(theme), [theme]);
  const { cardWidth, cardHeight } = useResponsiveCardSize();
  const listRef = useRef<FlatList<TopPickEntry>>(null);
  const stride = cardWidth + CARD_GAP;
  const reduceMotion = useReducedMotionPreference();

  // Subtle one-time entrance — fades and lifts the whole module in on its
  // first appearance only (never replays on re-render/re-rank), and is
  // skipped entirely under Reduce Motion.
  const entrance = useRef(new Animated.Value(reduceMotion ? 1 : 0)).current;
  const hasAnimatedIn = useRef(false);
  useEffect(() => {
    if (status !== 'ready' || items.length === 0 || hasAnimatedIn.current) return;
    hasAnimatedIn.current = true;
    if (reduceMotion) {
      entrance.setValue(1);
      return;
    }
    Animated.timing(entrance, { toValue: 1, duration: 420, useNativeDriver: true }).start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, items.length, reduceMotion]);

  const withReasons = useMemo(
    () => items.map(item => ({ item, reason: pickRecommendationReason(item, profile, mode) })),
    [items, profile, mode],
  );

  const renderItem = useCallback(({ item }: { item: TopPickEntry }) => (
    <TopPickWideCard
      activity={item.item.activity}
      reason={item.reason}
      userId={userId}
      width={cardWidth}
      height={cardHeight}
      onPress={onCardPress}
    />
  ), [cardWidth, cardHeight, onCardPress, userId]);

  if (status === 'loading') return <TopPicksModuleSkeleton cardWidth={cardWidth} cardHeight={cardHeight} />;

  if (status === 'error') {
    return (
      <View style={[moduleStyles.container, themed.container]} testID={testID}>
        <ModuleHeader mode={mode} onSeeAll={onSeeAll} showSeeAll={false} />
        <View style={[moduleStyles.errorRow, themed.errorRow]}>
          <Text style={[moduleStyles.errorText, themed.textSecondary]}>{errorMessage}</Text>
          <TouchableOpacity onPress={onRetry} activeOpacity={0.8} accessibilityRole="button">
            <Text style={moduleStyles.retryText}>{retryLabel}</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  // Hide the whole module — no empty container, no orphaned heading — when
  // there's nothing to recommend.
  if (items.length === 0) return null;

  return (
    <Animated.View
      style={[
        moduleStyles.container,
        themed.container,
        // No entrance animation on this interactive module: on iOS a native-driven
        // fade/slide left React's idea of its position out of sync with the screen,
        // so taps on cards and "See all" were cancelled as "outside" the touchable.
      ]}
      testID={testID}
    >
      <ModuleHeader mode={mode} onSeeAll={onSeeAll} showSeeAll />

      <FlatList
        ref={listRef}
        data={withReasons}
        horizontal
        nestedScrollEnabled
        showsHorizontalScrollIndicator={false}
        keyExtractor={entry => entry.item.activity.id}
        renderItem={renderItem}
        contentContainerStyle={moduleStyles.list}
        ItemSeparatorComponent={CardSeparator}
        decelerationRate="fast"
        snapToInterval={stride}
        snapToAlignment="start"
        getItemLayout={(_, index) => ({ length: stride, offset: stride * index, index })}
        initialNumToRender={3}
        maxToRenderPerBatch={3}
        windowSize={5}
      />
    </Animated.View>
  );
};

const CardSeparator = () => <View style={{ width: CARD_GAP }} />;

const ModuleHeader: React.FC<{ mode: TopPicksMode; onSeeAll: () => void; showSeeAll: boolean }> = ({ mode, onSeeAll, showSeeAll }) => {
  const { t } = useI18n();
  const { theme } = useTheme();
  const themed = useMemo(() => createModuleThemeStyles(theme), [theme]);
  const title = mode === 'personalized' ? t('feed.topPicksForYou') : t('feed.popularNearYou');
  const subtitle = mode === 'personalized'
    ? t('feed.topPicksSubtitle', 'Events matched to your interests')
    : t('feed.topPicksSubtitlePopular', 'Trending events near you');
  const seeAllLabel = t('feed.topPicksSeeAll', 'See all');

  return (
    <View style={moduleStyles.header}>
      <View style={moduleStyles.headerCopy}>
        <Text style={[moduleStyles.title, themed.textPrimary]} numberOfLines={1}>{title}</Text>
        <Text style={[moduleStyles.subtitle, themed.textSecondary]} numberOfLines={1}>{subtitle}</Text>
      </View>
      {showSeeAll ? (
        <TouchableOpacity
          onPress={onSeeAll}
          activeOpacity={0.75}
          style={moduleStyles.seeAllButton}
          accessibilityRole="button"
          accessibilityLabel={seeAllLabel}
        >
          <Text style={moduleStyles.seeAllText}>{seeAllLabel}</Text>
          <Icon name="chevron-right" size={14} color={Colors.sunset} />
        </TouchableOpacity>
      ) : null}
    </View>
  );
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const moduleStyles = StyleSheet.create({
  container: {
    // Extra vertical breathing room above/below, well beyond a plain
    // carousel's spacing, so this reads as its own distinct module rather
    // than blending into the surrounding feed cards.
    marginTop: Spacing.xl,
    marginBottom: Spacing.xxl,
    borderRadius: Radius.xxl,
    borderWidth: 1,
    borderColor: 'rgba(242,125,38,0.30)',
    backgroundColor: 'rgba(242,125,38,0.16)',
    paddingVertical: Spacing.lg,
    paddingHorizontal: CONTAINER_INNER_PADDING,
    overflow: 'hidden',
    ...Shadow.glow,
    // Android elevation renders as a hard rectangular layer around this
    // large clipped section. Use a broad, soft orange shadow instead.
    elevation: 0,
    shadowOpacity: 0.18,
    shadowRadius: 28,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.sm,
    marginBottom: Spacing.md,
    paddingHorizontal: Spacing.xs,
  },
  headerCopy: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    color: '#fff',
    fontSize: 19,
    lineHeight: 23,
    fontWeight: Typography.extrabold,
  },
  subtitle: {
    color: '#fff',
    marginTop: 2,
    fontSize: 12,
    lineHeight: 16,
    fontWeight: Typography.medium,
  },
  seeAllButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    paddingVertical: 4,
    paddingLeft: 8,
  },
  seeAllText: {
    color: Colors.sunset,
    fontSize: 13,
    fontWeight: Typography.bold,
  },
  list: {
    gap: 0,
    paddingLeft: Spacing.xs,
    // The extra tail padding is what gives the final card real breathing
    // room instead of butting against the container edge.
    paddingRight: Spacing.xs + LAST_CARD_EXTRA_PADDING,
  },
  skeletonCardRow: {
    paddingLeft: Spacing.xs,
  },
  // Midnight colors — the canonical implementation (see daylightStyle.ts);
  // createModuleThemeStyles' `skeletonFill` only ever overrides these for
  // daylight.
  skeletonFill: {
    backgroundColor: '#0f1520',
    borderColor: 'rgba(255,255,255,0.1)',
  },
  skeletonTitle: {
    width: 150,
    height: 17,
    borderRadius: 8,
    marginBottom: 6,
  },
  skeletonSubtitle: {
    width: 190,
    height: 13,
    borderRadius: 6,
  },
  errorRow: {
    minHeight: 72,
    borderRadius: 16,
    borderWidth: 1,
    // Midnight colors — the canonical implementation (see daylightStyle.ts);
    // createModuleThemeStyles' `errorRow` only ever overrides these for
    // daylight.
    backgroundColor: '#0f1520',
    borderColor: 'rgba(255,255,255,0.1)',
    paddingHorizontal: 14,
    paddingVertical: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  errorText: {
    flex: 1,
    fontSize: 12,
    lineHeight: 17,
    fontWeight: Typography.medium,
  },
  retryText: {
    color: Colors.sunset,
    fontSize: 12,
    fontWeight: Typography.bold,
  },
});

const cardStyles = StyleSheet.create({
  // Shape only, reused by the skeleton placeholder with a neutral fill
  // instead of the real card's photo-card colors below.
  cardShape: {
    borderRadius: Radius.xl,
    overflow: 'hidden',
    borderWidth: 1,
  },
  card: {
    borderColor: 'rgba(255,255,255,0.14)',
    backgroundColor: 'rgba(255,255,255,0.06)',
    ...Shadow.card,
    // Avoid the hard rectangular elevation layer visible against the orange
    // module; retain only a subtle, broadly blurred dark shadow.
    elevation: 0,
    shadowOpacity: 0.16,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: 8 },
  },
  image: {
    ...StyleSheet.absoluteFill,
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  fallbackGlow: {
    top: -60,
    left: -40,
  },
  topRow: {
    position: 'absolute',
    top: 12,
    left: 12,
    right: 12,
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 8,
  },
  topLeftStack: {
    gap: 6,
    flexShrink: 1,
  },
  forYouBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 4,
    borderRadius: Radius.pill,
    borderWidth: 1,
    borderColor: 'rgba(242,125,38,0.55)',
    backgroundColor: 'rgba(242,125,38,0.24)',
    paddingHorizontal: 9,
    paddingVertical: 4,
  },
  forYouBadgeText: {
    color: '#ffe3c9',
    fontSize: 10,
    lineHeight: 12,
    fontWeight: Typography.extrabold,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  reasonChip: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    maxWidth: 210,
    gap: 4,
    borderRadius: Radius.pill,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.18)',
    backgroundColor: 'rgba(10,14,22,0.62)',
    paddingHorizontal: 9,
    paddingVertical: 4,
  },
  reasonChipText: {
    color: 'rgba(255,225,200,0.95)',
    fontSize: 10,
    lineHeight: 12,
    fontWeight: Typography.bold,
  },
  heartButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.22)',
    backgroundColor: 'rgba(10,14,22,0.55)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  heartButtonActive: {
    borderColor: 'rgba(242,125,38,0.5)',
    backgroundColor: 'rgba(242,125,38,0.78)',
  },
  bottomCopy: {
    position: 'absolute',
    left: 14,
    right: 14,
    bottom: 12,
  },
  title: {
    color: '#fff',
    fontSize: 17,
    lineHeight: 21,
    fontWeight: Typography.extrabold,
    textShadowColor: 'rgba(0,0,0,0.5)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 3,
  },
  metaRow: {
    marginTop: 5,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  metaText: {
    flex: 1,
    color: 'rgba(255,255,255,0.86)',
    fontSize: 12,
    lineHeight: 15,
    fontWeight: Typography.medium,
  },
  partnerText: {
    flex: 1,
    color: 'rgba(255,255,255,0.72)',
    fontSize: 11,
    lineHeight: 14,
    fontWeight: Typography.bold,
  },
});

// Midnight (dark) colors live directly on cardStyles/moduleStyles above —
// the canonical implementation, same convention as every other themed
// component in this app (see daylightStyle.ts): createThemeStyles below
// only ever overrides these for the daylight theme.
const createModuleThemeStyles = (theme: TonyteTheme) => StyleSheet.create({
  container: daylightStyle(theme, {
    borderColor: 'rgba(242,125,38,0.32)',
    backgroundColor: 'rgba(242,125,38,0.14)',
  }),
  textPrimary: daylightStyle(theme, { color: theme.colors.textPrimary }),
  textSecondary: daylightStyle(theme, { color: theme.colors.textSecondary }),
  errorRow: daylightStyle(theme, {
    backgroundColor: theme.colors.surface,
    borderColor: theme.colors.border,
  }),
  skeletonFill: daylightStyle(theme, {
    backgroundColor: theme.colors.surface,
    borderColor: theme.colors.border,
  }),
});

const createCardThemeStyles = (_theme: TonyteTheme) => StyleSheet.create({
  // The wide card is a photo card (image or a dark gradient fallback) with
  // a bottom scrim for text legibility, same as every other Tonyte event
  // card — deliberately identical in both themes, matching EventCarousel's
  // card and EventCard's own image-card treatment.
  card: {},
});

export default memo(TopPicksModule);
